import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#18181B', '#71717A', '#A1A1AA', '#D4D4D8', '#E4E4E7'];

export default function AnalyticsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load analytics data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </Layout>
    );
  }

  const vendorData = products.reduce((acc, product) => {
    const vendor = product.vendor || 'Unknown';
    if (!acc[vendor]) {
      acc[vendor] = { vendor, count: 0, value: 0 };
    }
    acc[vendor].count += 1;
    acc[vendor].value += product.inventory * product.purchase_price;
    return acc;
  }, {});

  const vendorChartData = Object.values(vendorData).sort((a, b) => b.count - a.count).slice(0, 5);

  const stockDistribution = [
    { name: 'Critical (<25)', value: products.filter((p) => p.inventory < 25).length },
    { name: 'Low (25-50)', value: products.filter((p) => p.inventory >= 25 && p.inventory < 50).length },
    { name: 'Medium (50-100)', value: products.filter((p) => p.inventory >= 50 && p.inventory < 100).length },
    { name: 'Good (100+)', value: products.filter((p) => p.inventory >= 100).length },
  ];

  const topProducts = products
    .sort((a, b) => b.inventory * b.purchase_price - a.inventory * a.purchase_price)
    .slice(0, 10)
    .map((p) => ({
      name: p.sku,
      value: p.inventory * p.purchase_price,
    }));

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">Analytics & Insights</h1>
          <p className="text-muted-foreground mt-2">Visual overview of your inventory</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-border bg-card rounded-sm p-6">
            <h2 className="text-lg font-bold tracking-tight font-heading mb-6" data-testid="vendor-distribution-title">
              Top Vendors by Product Count
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={vendorChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="vendor"
                  stroke="hsl(var(--muted-foreground))"
                  style={{ fontSize: '12px', fontFamily: 'Inter' }}
                />
                <YAxis stroke="hsl(var(--muted-foreground))" style={{ fontSize: '12px', fontFamily: 'JetBrains Mono' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--primary))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '2px',
                  }}
                  labelStyle={{ color: 'hsl(var(--primary-foreground))', fontFamily: 'JetBrains Mono' }}
                  itemStyle={{ color: 'hsl(var(--primary-foreground))' }}
                />
                <Bar dataKey="count" fill="#18181B" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="border border-border bg-card rounded-sm p-6">
            <h2 className="text-lg font-bold tracking-tight font-heading mb-6" data-testid="stock-distribution-title">
              Stock Level Distribution
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stockDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stockDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--primary))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '2px',
                  }}
                  labelStyle={{ color: 'hsl(var(--primary-foreground))', fontFamily: 'JetBrains Mono' }}
                  itemStyle={{ color: 'hsl(var(--primary-foreground))' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="border border-border bg-card rounded-sm p-6 lg:col-span-2">
            <h2 className="text-lg font-bold tracking-tight font-heading mb-6" data-testid="top-products-title">
              Top 10 Products by Inventory Value
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topProducts} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="hsl(var(--muted-foreground))"
                  style={{ fontSize: '12px', fontFamily: 'JetBrains Mono' }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="hsl(var(--muted-foreground))"
                  style={{ fontSize: '11px', fontFamily: 'JetBrains Mono' }}
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--primary))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '2px',
                  }}
                  labelStyle={{ color: 'hsl(var(--primary-foreground))', fontFamily: 'JetBrains Mono' }}
                  itemStyle={{ color: 'hsl(var(--primary-foreground))' }}
                  formatter={(value) => `₹${value.toLocaleString()}`}
                />
                <Bar dataKey="value" fill="#18181B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="border border-border bg-card rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Avg Stock/Product</p>
            <p className="text-2xl font-bold font-mono" data-testid="avg-stock">
              {products.length > 0 ? (products.reduce((sum, p) => sum + p.inventory, 0) / products.length).toFixed(0) : 0}
            </p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Avg Price/Unit</p>
            <p className="text-2xl font-bold font-mono" data-testid="avg-price">
              ₹{products.length > 0 ? (products.reduce((sum, p) => sum + p.purchase_price, 0) / products.length).toFixed(0) : 0}
            </p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Total Vendors</p>
            <p className="text-2xl font-bold font-mono" data-testid="total-vendors">
              {Object.keys(vendorData).length}
            </p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Stock Turnover</p>
            <p className="text-2xl font-bold font-mono" data-testid="stock-turnover">N/A</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}